import React, { useState, useMemo } from 'react';
import { useTranslations } from '../contexts/TranslationContext';
import { getGoalThemeOptions } from '../config/goalThemes';
import { useDraft } from '../contexts/DraftContext';

// Inline SVG icons (match the Hero Settings panel exactly).
const GearIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const ChevronUpIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m18 15-6-6-6 6" />
  </svg>
);
const ChevronDownIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

/**
 * Admin-only floating panel for the fundraising Goal section. Dark-glass style
 * matching the Hero Settings panel. Persists changes to the OrganizationGoal DB
 * row (single source of truth) via PUT admin/organization/goal.
 *
 * Props:
 *   mainDraw – resolved next-draw payload carrying goal_* fields
 *   onSaved  – optional callback after a successful save (e.g. refetch)
 */
export default function GoalThermometerAdminPanel({ mainDraw, onSettingsChanged }) {
  const { t: tr } = useTranslations();
  const draft = useDraft();
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);

  const themeOptions = useMemo(() => getGoalThemeOptions(), []);

  // Local editable state seeded from draft or the resolved goal.
  const [enabled, setEnabled] = useState(() => draft.getDraftAppearance('goalEnabled') ?? !!mainDraw?.goal_enabled);
  const [theme, setTheme] = useState(() => draft.getDraftAppearance('goalTheme') ?? mainDraw?.goal_theme ?? 'classic-red');
  const [amount, setAmount] = useState(() => draft.getDraftAppearance('goalAmount') ?? (Number(mainDraw?.goal_amount) || 0));
  const [title, setTitle] = useState(() => draft.getDraftAppearance('goalTitle') ?? mainDraw?.goal_title ?? '');
  const [refreshSeconds, setRefreshSeconds] = useState(() => Math.round((draft.getDraftAppearance('goalRefreshMs') ?? mainDraw?.goal_refresh_ms ?? 30000) / 1000));

  const save = () => {
    // Update draft for publish
    draft.updateDraftAppearance('goalEnabled', enabled);
    draft.updateDraftAppearance('goalTheme', theme);
    draft.updateDraftAppearance('goalAmount', Number(amount) || 0);
    draft.updateDraftAppearance('goalTitle', title);
    draft.updateDraftAppearance('goalRefreshMs', Math.max(5, Number(refreshSeconds) || 30) * 1000);
    setSaved(true);
    if (typeof onSettingsChanged === 'function') {
      onSettingsChanged({ enabled, theme, amount: Number(amount) || 0, title, refreshMs: Math.max(5, Number(refreshSeconds) || 30) * 1000 });
    }
  };

  const panelTitle = tr('admin.goalSettings.title', 'Goal Settings');
  const showLabel = tr('admin.goalSettings.showGoal', 'Show goal');
  const themeLabel = tr('admin.goalSettings.theme', 'Theme');
  const amountLabel = tr('admin.goalSettings.amount', 'Goal amount');
  const titleLabel = tr('admin.goalSettings.goalTitle', 'Title');

  return (
    <div className="absolute top-0 left-1/2 -translate-x-1/2 z-[100] flex flex-col items-center">
      {open ? (
        <div className="rounded-b-xl bg-black/80 backdrop-blur-md border border-t-0 border-white/20 px-4 pt-2 pb-3 flex flex-col gap-2 min-w-[300px] shadow-xl max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] font-semibold text-white/60 uppercase tracking-wider flex items-center gap-1">
              <GearIcon className="w-3 h-3" /> {panelTitle}
            </span>
            <button
              onClick={() => setOpen(false)}
              className="p-0.5 rounded hover:bg-white/15 text-white/70 hover:text-white transition-colors"
              aria-label={panelTitle}
              title={panelTitle}
            >
              <ChevronUpIcon className="w-4 h-4" />
            </button>
          </div>

            {/* Visibility */}
            <label className="flex items-center justify-between gap-2 cursor-pointer group pt-1 border-t border-white/10">
              <span className="text-xs text-white/70 group-hover:text-white transition-colors">{showLabel}</span>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                onClick={() => { const v = !enabled; setEnabled(v); }}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-white/20 transition-colors ${enabled ? 'bg-emerald-500/80' : 'bg-white/15'}`}
              >
                <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </label>

            {/* Amount */}
            <label className="flex flex-col gap-1 pt-1 border-t border-white/10">
              <span className="text-[10px] font-semibold text-white/60 uppercase tracking-wider">{amountLabel}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded-lg bg-white/10 border border-white/20 px-3 py-1.5 text-sm text-white focus:outline-none focus:border-white/40"
              />
            </label>

            {/* Title */}
            <label className="flex flex-col gap-1 pt-1 border-t border-white/10">
              <span className="text-[10px] font-semibold text-white/60 uppercase tracking-wider">{titleLabel}</span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={tr('admin.goalSettings.titlePlaceholder', 'Our Goal')}
                className="w-full rounded-lg bg-white/10 border border-white/20 px-3 py-1.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40"
              />
            </label>

            {/* Theme picker */}
            <div className="flex flex-col gap-1.5 pt-1 border-t border-white/10">
              <span className="text-[10px] font-semibold text-white/60 uppercase tracking-wider">{themeLabel}</span>
              <div className="grid grid-cols-3 gap-1.5">
                {themeOptions.map((opt) => {
                  const localizedLabel = tr(`admin.goalSettings.themes.${opt.value}`, opt.label);
                  return (
                  <button
                    key={opt.value}
                    onClick={() => setTheme(opt.value)}
                    className={`rounded-lg px-2 py-1.5 text-[10px] font-semibold border transition-colors ${theme === opt.value ? 'bg-white/25 border-white/50 text-white' : 'bg-white/5 border-white/10 text-white/60 hover:text-white/80'}`}
                    title={localizedLabel}
                  >
                    {localizedLabel}
                  </button>
                  );
                })}
              </div>
            </div>

            {/* Refresh interval */}
            <label className="flex flex-col gap-1 pt-1 border-t border-white/10">
              <span className="text-[10px] font-semibold text-white/60 uppercase tracking-wider">{tr('admin.goalSettings.updateInterval', 'Update interval (seconds)')}</span>
              <input
                type="number"
                min={5}
                step={5}
                value={refreshSeconds}
                onChange={(e) => { setRefreshSeconds(e.target.value); setSaved(false); }}
                className="w-full rounded-lg bg-white/10 border border-white/20 px-3 py-1.5 text-sm text-white focus:outline-none focus:border-white/40"
              />
              <p className="text-[10px] text-white/40">{tr('admin.goalSettings.minSeconds', 'Minimum 5 seconds')}</p>
            </label>

            <button
              type="button"
              onClick={() => save()}
              className="mt-1 w-full rounded-lg py-1.5 text-xs font-semibold text-white"
              style={{ background: 'var(--charity-primary, #e75480)' }}
            >
              {saved ? tr('admin.goalSettings.savedToDraft', '✓ Saved to draft') : tr('admin.goalSettings.saveSettings', 'Save Settings')}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setOpen(true)}
            className="rounded-b-lg bg-black/60 backdrop-blur-md border border-t-0 border-white/20 px-3 py-1 text-[10px] font-semibold text-white/60 hover:text-white hover:bg-black/80 transition-colors flex items-center gap-1"
            title={panelTitle}
          >
            <GearIcon className="w-3 h-3" /> <ChevronDownIcon className="w-3 h-3" />
          </button>
        )}
    </div>
  );
}
